import { HugeiconsIcon } from "@hugeicons/react";
import {
  Download04Icon,
  PackageIcon,
  Refresh01Icon,
  CheckmarkCircle02Icon,
  Alert02Icon,
  Copy01Icon,
} from "@hugeicons/core-free-icons";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  useCaptainHookVersion,
  getCaptainHookDownloadUrl,
} from "@/features/captainHook";

export default function ProgramsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } =
    useCaptainHookVersion();

  const handleDownload = () => {
    window.open(getCaptainHookDownloadUrl(), "_blank", "noopener");
  };

  const handleCopySha = async () => {
    if (!data?.sha256) return;
    await navigator.clipboard.writeText(data.sha256);
    toast.success("SHA-256 값을 복사했습니다.");
  };

  return (
    <div className="flex h-full flex-col p-4 bg-slate-50/50">
      {/* Page Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">프로그램 배포</h1>
          <p className="mt-0.5 text-xs text-gray-600">
            설치 프로그램의 최신 버전을 확인하고 내려받습니다
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <HugeiconsIcon
            icon={Refresh01Icon}
            size={14}
            className={isFetching ? "animate-spin" : ""}
          />
          새로고침
        </Button>
      </div>

      {/* CaptainHook 카드 */}
      <div className="max-w-2xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <HugeiconsIcon icon={PackageIcon} size={24} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900">CaptainHook</h2>
              <span className="text-xs text-gray-500">
                카카오톡 SNS 수집기 (Windows)
              </span>
            </div>

            {isLoading && (
              <p className="mt-3 text-sm text-gray-500">
                최신 버전 정보를 불러오는 중…
              </p>
            )}

            {isError && (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
                <HugeiconsIcon icon={Alert02Icon} size={16} />
                <span>
                  버전 정보를 불러오지 못했습니다: {(error as Error)?.message}
                </span>
              </div>
            )}

            {data && (
              <>
                <div className="mt-3 flex items-center gap-2">
                  <HugeiconsIcon
                    icon={CheckmarkCircle02Icon}
                    size={16}
                    className="text-green-600"
                  />
                  <span className="text-sm text-gray-600">최신 버전</span>
                  <span className="rounded-md bg-gray-900 px-2 py-0.5 text-sm font-bold text-white">
                    v{data.version}
                  </span>
                </div>

                {data.changelog && (
                  <p className="mt-2 whitespace-pre-line text-sm text-gray-600">
                    {data.changelog}
                  </p>
                )}

                {data.sha256 && (
                  <button
                    type="button"
                    onClick={handleCopySha}
                    title="클릭하여 복사"
                    className="mt-3 flex max-w-full items-center gap-1.5 text-left text-xs text-gray-400 transition-colors hover:text-gray-600"
                  >
                    <HugeiconsIcon icon={Copy01Icon} size={12} className="shrink-0" />
                    <span className="truncate font-mono">
                      SHA-256: {data.sha256}
                    </span>
                  </button>
                )}

                <div className="mt-5">
                  <Button
                    onClick={handleDownload}
                    className="bg-blue-600 font-bold hover:bg-blue-700"
                  >
                    <HugeiconsIcon icon={Download04Icon} size={16} />
                    최신 버전 다운로드
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
